<template>
  <div class="page-body">
    <div class="container-xl">
      <div class="page-header d-print-none mb-3">
        <div class="row align-items-center">
          <div class="col">
            <div class="page-pretitle">single sign-on</div>
            <h2 class="page-title">OIDC Clients</h2>
          </div>
          <div class="col-auto">
            <button class="btn btn-success" @click="openCreate">
              <svg xmlns="http://www.w3.org/2000/svg" class="icon" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              New client
            </button>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div class="container" style="overflow-x: auto; padding: 0;">
            <table class="table table-xl">
              <thead>
                <tr>
                  <th>Client ID</th>
                  <th>Name</th>
                  <th>Redirect URIs</th>
                  <th>Post-logout URIs</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="client of clients" :key="client.client_id">
                  <td><code>{{ client.client_id }}</code></td>
                  <td>{{ client.name }}</td>
                  <td>
                    <div v-for="uri of client.redirect_uris" :key="uri" class="text-secondary small">{{ uri }}</div>
                  </td>
                  <td>
                    <div v-for="uri of (client.post_logout_redirect_uris || [])" :key="uri" class="text-secondary small">{{ uri }}</div>
                    <span v-if="!(client.post_logout_redirect_uris || []).length" class="text-secondary">—</span>
                  </td>
                  <td class="text-secondary">{{ formatDate(client.created_at) }}</td>
                  <td class="text-end text-nowrap">
                    <button class="btn me-2" @click="openEdit(client)">Edit</button>
                    <button class="btn btn-danger" @click="remove(client)">Delete</button>
                  </td>
                </tr>
                <tr v-if="!clients.length && !loading">
                  <td colspan="6" class="text-secondary text-center py-4">No OIDC clients yet. Create one to let an app sign in via SKAARHOJ.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / edit client modal -->
    <div class="modal modal-blur fade" :class="{ show: showForm }" :style="{ display: showForm ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editing ? 'Edit OIDC client' : 'New OIDC client' }}</h5>
            <button type="button" class="btn-close" @click="closeForm"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label required">Client ID</label>
              <input v-model.trim="form.client_id" type="text" class="form-control" placeholder="grafana" autocomplete="off" :readonly="!!editing" />
              <small class="form-hint">{{ editing ? 'The client ID cannot be changed.' : 'Stable identifier the client authenticates with. Lowercase, no spaces.' }}</small>
            </div>
            <div class="mb-3">
              <label class="form-label">Name</label>
              <input v-model.trim="form.name" type="text" class="form-control" placeholder="Grafana (staging)" />
            </div>
            <div class="mb-3">
              <label class="form-label required">Redirect URIs</label>
              <textarea v-model="form.redirect_uris" class="form-control" rows="2" placeholder="https://monitoring.skaarhoj.com/login/generic_oauth" spellcheck="false"></textarea>
              <small class="form-hint">One per line. Must match the client's callback URL exactly.</small>
            </div>
            <div class="mb-3">
              <label class="form-label">Post-logout redirect URIs</label>
              <textarea v-model="form.post_logout_redirect_uris" class="form-control" rows="2" placeholder="https://monitoring.skaarhoj.com/" spellcheck="false"></textarea>
              <small class="form-hint">One per line. Optional — where the browser may return after logout.</small>
            </div>
            <div class="mb-1">
              <label class="form-label" :class="{ required: !editing }">{{ editing ? 'Rotate client secret' : 'Client secret' }}</label>
              <div class="input-group">
                <input v-model="form.secret" type="text" class="form-control font-monospace" spellcheck="false" :placeholder="editing ? 'Leave blank to keep the current secret' : ''" />
                <button class="btn btn-outline-secondary" type="button" @click="form.secret = genSecret()">{{ editing ? 'Generate new' : 'Regenerate' }}</button>
              </div>
              <small class="form-hint">{{ editing ? 'Set a value only to rotate the secret. It is hashed on save and shown once below.' : 'Auto-generated. It is hashed on save and shown to you only once below.' }}</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn" @click="closeForm">Cancel</button>
            <button type="button" class="btn btn-primary" :disabled="submitting" @click="save">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editing ? 'Save changes' : 'Create client' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Secret-shown-once modal -->
    <div class="modal modal-blur fade" :class="{ show: !!created }" :style="{ display: created ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Client created</h5>
            <button type="button" class="btn-close" @click="created = null"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              Copy the secret now — it is stored hashed and <strong>cannot be shown again</strong>.
            </div>
            <div class="mb-3">
              <label class="form-label">Client ID</label>
              <div class="input-group">
                <input :value="created?.client_id" type="text" class="form-control font-monospace" readonly />
                <button class="btn btn-outline-secondary" type="button" @click="copy(created.client_id)">Copy</button>
              </div>
            </div>
            <div class="mb-1">
              <label class="form-label">Client secret</label>
              <div class="input-group">
                <input :value="created?.secret" type="text" class="form-control font-monospace" readonly />
                <button class="btn btn-outline-secondary" type="button" @click="copy(created.secret)">Copy</button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="created = null">Done</button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-backdrop fade show" v-if="showForm || created"></div>
  </div>
</template>

<script setup>
import { getCurrentInstance, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'

const $vue = getCurrentInstance()
const store = useStore()

const clients = ref([])
const loading = ref(false)
const showForm = ref(false)
const editing = ref(null)
const submitting = ref(false)
const created = ref(null)

const form = reactive({
  client_id: '',
  name: '',
  redirect_uris: '',
  post_logout_redirect_uris: '',
  secret: ''
})

function genSecret () {
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
}

function formatDate (value) {
  if (!value) return ''
  return new Date(value).toLocaleString()
}

function linesToArray (text) {
  return text.split('\n').map(s => s.trim()).filter(Boolean)
}

function loadClients () {
  loading.value = true
  store.dispatch('getOidcClients')
    .then(data => { clients.value = data.Data || [] })
    .catch(() => { $vue?.proxy.$swal.fire({ icon: 'error', text: 'Failed to load OIDC clients' }) })
    .finally(() => { loading.value = false })
}

function openCreate () {
  editing.value = null
  form.client_id = ''
  form.name = ''
  form.redirect_uris = ''
  form.post_logout_redirect_uris = ''
  form.secret = genSecret()
  showForm.value = true
}

function openEdit (client) {
  editing.value = client
  form.client_id = client.client_id
  form.name = client.name || ''
  form.redirect_uris = (client.redirect_uris || []).join('\n')
  form.post_logout_redirect_uris = (client.post_logout_redirect_uris || []).join('\n')
  form.secret = ''
  showForm.value = true
}

function closeForm () {
  showForm.value = false
}

async function save () {
  const redirectUris = linesToArray(form.redirect_uris)
  if (!form.client_id || !redirectUris.length || (!editing.value && !form.secret)) {
    $vue?.proxy.$swal.fire({ icon: 'error', text: 'Client ID, secret and at least one redirect URI are required.' })
    return
  }
  const payload = {
    name: form.name,
    secret: form.secret,
    redirect_uris: redirectUris,
    post_logout_redirect_uris: linesToArray(form.post_logout_redirect_uris)
  }
  submitting.value = true
  try {
    if (editing.value) {
      await store.dispatch('updateOidcClient', { clientId: form.client_id, data: payload })
    } else {
      await store.dispatch('createOidcClient', { client_id: form.client_id, ...payload })
    }
    // Only surface the secret when one was actually set (always on create,
    // only on a rotation for edits).
    if (form.secret) {
      created.value = { client_id: form.client_id, secret: form.secret }
    }
    showForm.value = false
    loadClients()
  } catch (err) {
    $vue?.proxy.$swal.fire({ icon: 'error', text: err?.response?.data?.Data || 'Failed to save client' })
  } finally {
    submitting.value = false
  }
}

async function remove (client) {
  const res = await $vue?.proxy.$swal.fire({
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'No, cancel!',
    text: 'Delete OIDC client "' + client.client_id + '"? Apps using it will no longer be able to sign in.'
  })
  if (!res.isConfirmed) return
  try {
    await store.dispatch('removeOidcClient', client.client_id)
    loadClients()
  } catch (err) {
    $vue?.proxy.$swal.fire({ icon: 'error', text: err?.response?.data?.Data || 'Failed to delete client' })
  }
}

async function copy (text) {
  try {
    await navigator.clipboard.writeText(text)
    $vue?.proxy.$swal.fire({ icon: 'success', timer: 900, showConfirmButton: false, text: 'Copied' })
  } catch {
    // clipboard unavailable — no-op
  }
}

onMounted(loadClients)
</script>

<style scoped>
.table code {
  font-size: 0.85rem;
}
</style>
